import Popper from '@mui/material/Popper';
import is from '@sindresorhus/is';
import type { FunctionComponent, RefObject } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import {
    GlobalSearchDropdownContainer,
    GlobalSearchDropdownEmpty,
    GlobalSearchDropdownItem,
    GlobalSearchDropdownLoading,
    GlobalSearchDropdownSeparator,
} from '~/ui/domain/global-search/GlobalSearchDropdown';

import type {
    GlobalSearch,
    GlobalSearchJobLevelResponse,
    GlobalSearchJobRoleResponse,
} from '../../data-contract/global-search';
import { useNavigationToEntity } from '../common/use-navigation-to-entity';
import { DropdownSection } from './DropDownSection';
import { buildJobLevelSearchResultUrl, buildJobRoleSearchResultUrl } from './job-architecture-result-url';
import type { TabParam } from './use-global-search-table-params';

const JOBS_TAB = 'jobs';
const SKILLS_TAB = 'skills';
const EMPLOYEES_TAB = 'employees';
const LEARNING_OPPORTUNITIES_TAB = 'learningOpportunities';

type DropdownItemProps = {
    readonly isSelected: boolean;
    readonly onClick: () => void;
    readonly searchTermToHighlight: string;
    readonly title: string;
};

const DropdownItem: FunctionComponent<DropdownItemProps> = ({ isSelected, onClick, searchTermToHighlight, title }) => (
    <GlobalSearchDropdownItem isSelected={isSelected} onClick={onClick}>
        <Highlighter
            autoEscape={true}
            highlightTag="strong"
            searchWords={[searchTermToHighlight]}
            textToHighlight={title}
        />
    </GlobalSearchDropdownItem>
);

export type DropdownData = {
    readonly id: string;
} & (
    | { readonly type: 'job' | 'learning-opportunity' | 'skill' | 'user' }
    | {
          readonly clusterId?: string | null;
          readonly familyId?: string | null;
          readonly jobArchitectureId: string;
          readonly type: 'job-role';
      }
    | {
          readonly clusterId?: string | null;
          readonly familyId?: string | null;
          readonly jobArchitectureId: string;
          readonly roleId: string;
          readonly type: 'job-level';
      }
);

type DropdownContentProps = {
    readonly activeId: string;
    readonly addSkillToSearch: (skillId: string) => void;
    readonly handleGlobalSearch: (sectionValue: TabParam) => void;
    readonly jobLevelsResult: GlobalSearchJobLevelResponse | undefined;
    readonly jobRolesResult: GlobalSearchJobRoleResponse | undefined;
    readonly onCloseDropdown: () => void;
    readonly result: GlobalSearch | undefined;
    readonly selectedSkillIds: ReadonlySet<string>;
    readonly setSearchTerm: (searchTerm: string) => void;
};

const formatContextTitle = (title: string, context: readonly (string | null | undefined)[]): string => {
    const contextTitle = context.filter(is.nonEmptyString).join(' / ');

    return is.nonEmptyString(contextTitle) ? `${title} - ${contextTitle}` : title;
};

const hasAny = (values: readonly boolean[]): boolean => values.some(Boolean);

const hasAnyResults = (
    result: GlobalSearch | undefined,
    jobRolesResult: GlobalSearchJobRoleResponse | undefined,
    jobLevelsResult: GlobalSearchJobLevelResponse | undefined,
): boolean =>
    hasAny([
        is.nonEmptyArray(result?.skills?.data) || result?.skills?.failed === true,
        is.nonEmptyArray(result?.jobs?.data),
        is.nonEmptyArray(result?.users?.data),
        is.nonEmptyArray(result?.learningOpportunities?.data),
        is.nonEmptyArray(jobRolesResult?.data),
        is.nonEmptyArray(jobLevelsResult?.data),
    ]);

const hasAnyResponse = (
    result: GlobalSearch | undefined,
    jobRolesResult: GlobalSearchJobRoleResponse | undefined,
    jobLevelsResult: GlobalSearchJobLevelResponse | undefined,
): boolean => ![result, jobRolesResult, jobLevelsResult].every(is.undefined);

type JobArchitectureSectionProps = {
    readonly activeId: string;
    readonly clearSearch: () => void;
    readonly searchTermToHighlight: string;
};

const JobRolesSection: FunctionComponent<
    JobArchitectureSectionProps & { readonly jobRolesResult: GlobalSearchJobRoleResponse }
> = ({ activeId, clearSearch, jobRolesResult, searchTermToHighlight }) => {
    const { actions, translate } = useApplicationContext();

    return (
        <DropdownSection hasMore={false} label={translate('globalsearch.job-roles')} onClick={clearSearch}>
            {jobRolesResult.data.map((role) => (
                <DropdownItem
                    key={`search-${role.id}`}
                    isSelected={role.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={formatContextTitle(role.title, [
                        role.jobArchitectureTitle,
                        role.familyTitle,
                        role.clusterTitle,
                    ])}
                    onClick={() => {
                        actions.navigateTo(buildJobRoleSearchResultUrl(role));
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

const JobLevelsSection: FunctionComponent<
    JobArchitectureSectionProps & { readonly jobLevelsResult: GlobalSearchJobLevelResponse }
> = ({ activeId, clearSearch, jobLevelsResult, searchTermToHighlight }) => {
    const { actions, translate } = useApplicationContext();

    return (
        <DropdownSection hasMore={false} label={translate('globalsearch.job-levels')} onClick={clearSearch}>
            {jobLevelsResult.data.map((level) => (
                <DropdownItem
                    key={`search-${level.id}`}
                    isSelected={level.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={formatContextTitle(level.title, [level.roleTitle, level.jobArchitectureTitle])}
                    onClick={() => {
                        actions.navigateTo(buildJobLevelSearchResultUrl(level));
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

type DefaultSearchSectionProps = {
    readonly activeId: string;
    readonly clearSearch: () => void;
    readonly searchTermToHighlight: string;
};

const SkillsSection: FunctionComponent<
    DefaultSearchSectionProps & {
        readonly addSkillToSearch: (skillId: string) => void;
        readonly onSeeMore: () => void;
        readonly selectedSkillIds: ReadonlySet<string>;
        readonly skills: GlobalSearch['skills'];
    }
> = ({ activeId, addSkillToSearch, clearSearch, onSeeMore, searchTermToHighlight, selectedSkillIds, skills }) => {
    const translate = useTranslate();
    const selectableSkills = skills.data.filter((skill) => !selectedSkillIds.has(skill.id));

    return (
        <DropdownSection
            hasMore={skills.hasMore}
            label={skills.failed ? translate('globalsearch.skills-failed') : translate('globalsearch.skills')}
            onClick={onSeeMore}
        >
            {selectableSkills.map((skill) => (
                <DropdownItem
                    key={`search-${skill.id}`}
                    isSelected={skill.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={skill.title}
                    onClick={() => {
                        addSkillToSearch(skill.id);
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

const JobsSection: FunctionComponent<
    DefaultSearchSectionProps & {
        readonly jobs: GlobalSearch['jobs'];
        readonly onSeeMore: () => void;
    }
> = ({ activeId, clearSearch, jobs, onSeeMore, searchTermToHighlight }) => {
    const translate = useTranslate();
    const { navigateToJobPage } = useNavigationToEntity();

    return (
        <DropdownSection hasMore={jobs.hasMore} label={translate('globalsearch.jobs')} onClick={onSeeMore}>
            {jobs.data.map((job) => (
                <DropdownItem
                    key={`search-${job.id}`}
                    isSelected={job.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={job.title}
                    onClick={() => {
                        navigateToJobPage(job.id);
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

const UsersSection: FunctionComponent<
    DefaultSearchSectionProps & {
        readonly onSeeMore: () => void;
        readonly users: GlobalSearch['users'];
    }
> = ({ activeId, clearSearch, onSeeMore, searchTermToHighlight, users }) => {
    const translate = useTranslate();
    const { navigateToUserProfile } = useNavigationToEntity();

    return (
        <DropdownSection hasMore={users.hasMore} label={translate('globalsearch.users')} onClick={onSeeMore}>
            {users.data.map((user) => (
                <DropdownItem
                    key={`search-${user.id}`}
                    isSelected={user.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={`${user.firstName} ${user.lastName}`}
                    onClick={() => {
                        navigateToUserProfile(user.id);
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

const LearningOpportunitiesSection: FunctionComponent<
    DefaultSearchSectionProps & {
        readonly learningOpportunities: GlobalSearch['learningOpportunities'];
        readonly onSeeMore: () => void;
    }
> = ({ activeId, clearSearch, learningOpportunities, onSeeMore, searchTermToHighlight }) => {
    const translate = useTranslate();
    const { navigateToLearningOpportunityPage } = useNavigationToEntity();

    return (
        <DropdownSection
            hasMore={learningOpportunities.hasMore}
            label={translate('globalsearch.learning-opportunities')}
            onClick={onSeeMore}
        >
            {learningOpportunities.data.map((learningOpportunity) => (
                <DropdownItem
                    key={`search-${learningOpportunity.id}`}
                    isSelected={learningOpportunity.id === activeId}
                    searchTermToHighlight={searchTermToHighlight}
                    title={learningOpportunity.title}
                    onClick={() => {
                        navigateToLearningOpportunityPage(learningOpportunity.id);
                        clearSearch();
                    }}
                />
            ))}
        </DropdownSection>
    );
};

const DropdownContent: FunctionComponent<DropdownContentProps> = ({
    activeId,
    addSkillToSearch,
    handleGlobalSearch,
    jobLevelsResult,
    jobRolesResult,
    onCloseDropdown,
    result,
    selectedSkillIds,
    setSearchTerm,
}) => {
    if ([result, jobRolesResult, jobLevelsResult].every(is.undefined)) {
        return null;
    }

    const clearSearch = (): void => {
        setSearchTerm('');
        onCloseDropdown();
    };

    const redirect = (sectionValue: TabParam): void => {
        handleGlobalSearch(sectionValue);
    };

    const jobs = result?.jobs;
    const learningOpportunities = result?.learningOpportunities;
    const searchTerm = result?.searchTerm ?? jobRolesResult?.searchTerm ?? jobLevelsResult?.searchTerm;
    const skills = result?.skills;
    const users = result?.users;

    const searchTermToHighlight = is.nonEmptyString(searchTerm) ? searchTerm : '';
    const hasSkillsSection = is.nonEmptyArray(skills?.data) || skills?.failed === true;
    const hasJobsSection = is.nonEmptyArray(jobs?.data);
    const hasUsersSection = is.nonEmptyArray(users?.data);
    const hasLearningOpportunitiesSection = is.nonEmptyArray(learningOpportunities?.data);
    const hasJobRolesSection = is.nonEmptyArray(jobRolesResult?.data);
    const hasJobLevelsSection = is.nonEmptyArray(jobLevelsResult?.data);
    const hasDefaultSection = hasAny([
        hasSkillsSection,
        hasJobsSection,
        hasUsersSection,
        hasLearningOpportunitiesSection,
    ]);
    const hasSectionBeforeUsers = hasAny([hasSkillsSection, hasJobsSection]);
    const hasSectionBeforeLearningOpportunities = hasAny([hasSkillsSection, hasJobsSection, hasUsersSection]);
    const hasSectionBeforeJobLevels = hasAny([hasDefaultSection, hasJobRolesSection]);

    return (
        <Fragment>
            {hasSkillsSection && !is.undefined(skills) && (
                <SkillsSection
                    activeId={activeId}
                    addSkillToSearch={addSkillToSearch}
                    clearSearch={clearSearch}
                    searchTermToHighlight={searchTermToHighlight}
                    selectedSkillIds={selectedSkillIds}
                    skills={skills}
                    onSeeMore={() => redirect(SKILLS_TAB)}
                />
            )}
            {hasSkillsSection && hasJobsSection && <GlobalSearchDropdownSeparator />}
            {hasJobsSection && !is.undefined(jobs) && (
                <JobsSection
                    activeId={activeId}
                    clearSearch={clearSearch}
                    jobs={jobs}
                    searchTermToHighlight={searchTermToHighlight}
                    onSeeMore={() => redirect(JOBS_TAB)}
                />
            )}
            {hasSectionBeforeUsers && hasUsersSection && <GlobalSearchDropdownSeparator />}
            {hasUsersSection && !is.undefined(users) && (
                <UsersSection
                    activeId={activeId}
                    clearSearch={clearSearch}
                    searchTermToHighlight={searchTermToHighlight}
                    users={users}
                    onSeeMore={() => redirect(EMPLOYEES_TAB)}
                />
            )}
            {hasSectionBeforeLearningOpportunities && hasLearningOpportunitiesSection && (
                <GlobalSearchDropdownSeparator />
            )}
            {hasLearningOpportunitiesSection && !is.undefined(learningOpportunities) && (
                <LearningOpportunitiesSection
                    activeId={activeId}
                    clearSearch={clearSearch}
                    learningOpportunities={learningOpportunities}
                    searchTermToHighlight={searchTermToHighlight}
                    onSeeMore={() => redirect(LEARNING_OPPORTUNITIES_TAB)}
                />
            )}
            {hasDefaultSection && hasJobRolesSection && <GlobalSearchDropdownSeparator />}
            {hasJobRolesSection && !is.undefined(jobRolesResult) && (
                <JobRolesSection
                    activeId={activeId}
                    clearSearch={clearSearch}
                    jobRolesResult={jobRolesResult}
                    searchTermToHighlight={searchTermToHighlight}
                />
            )}
            {hasSectionBeforeJobLevels && hasJobLevelsSection && <GlobalSearchDropdownSeparator />}
            {hasJobLevelsSection && !is.undefined(jobLevelsResult) && (
                <JobLevelsSection
                    activeId={activeId}
                    clearSearch={clearSearch}
                    jobLevelsResult={jobLevelsResult}
                    searchTermToHighlight={searchTermToHighlight}
                />
            )}
        </Fragment>
    );
};

type DropdownProps = {
    readonly activeId: string;
    readonly addSkillToSearch: (skillId: string) => void;
    readonly handleGlobalSearch: (sectionValue: TabParam) => void;
    readonly isFetching: boolean;
    readonly jobLevelsResult: GlobalSearchJobLevelResponse | undefined;
    readonly jobRolesResult: GlobalSearchJobRoleResponse | undefined;
    readonly onCloseDropdown: () => void;
    readonly open: boolean;
    readonly positionRef: RefObject<HTMLElement | null>;
    readonly result: GlobalSearch | undefined;
    readonly searchTerm: string;
    readonly selectedSkillIds: ReadonlySet<string>;
    readonly setSearchTerm: (searchTerm: string) => void;
};

export const Dropdown: FunctionComponent<DropdownProps> = ({
    activeId,
    addSkillToSearch,
    handleGlobalSearch,
    isFetching,
    jobLevelsResult,
    jobRolesResult,
    onCloseDropdown,
    open,
    positionRef,
    result,
    searchTerm,
    selectedSkillIds,
    setSearchTerm,
}) => {
    const dropdownWidth = 400;

    const [oldSearchTerm, setOldSearchTerm] = useState<string>('');
    const previousResult = useRef<string>('');
    const [displayedResult, setDisplayedResult] = useState(result);
    const [showSpinner, setShowSpinner] = useState<boolean>(true);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        const isContinuedSearchTerm = searchTerm.startsWith(oldSearchTerm) || oldSearchTerm.startsWith(searchTerm);
        if (!isContinuedSearchTerm || searchTerm.trim() === '') {
            setShowSpinner(true);
            setDisplayedResult(undefined);
        }

        if (searchTerm !== previousResult.current) {
            setOldSearchTerm(previousResult.current);
            previousResult.current = searchTerm;
        }
    }, [searchTerm]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        if (isFetching === false && is.undefined(result) === false && result !== displayedResult) {
            setShowSpinner(false);
            setDisplayedResult(result);
        }
    }, [isFetching, result]);

    const translate = useTranslate();
    const showLoading = isFetching && showSpinner;
    const showResults = hasAnyResults(displayedResult, jobRolesResult, jobLevelsResult);
    const showEmpty = !showLoading && !showResults && hasAnyResponse(displayedResult, jobRolesResult, jobLevelsResult);

    if (!showLoading && !showResults && !showEmpty) {
        return null;
    }

    return (
        <Popper
            anchorEl={positionRef.current}
            id="global-search-dropdown"
            open={open}
            placement="bottom"
            style={{ width: dropdownWidth, zIndex: 2000 }}
        >
            <GlobalSearchDropdownContainer className="mt-xs">
                {showLoading && <GlobalSearchDropdownLoading />}
                {showEmpty && (
                    <GlobalSearchDropdownEmpty>{translate('globalsearch.no-results')}</GlobalSearchDropdownEmpty>
                )}
                {showResults && (
                    <DropdownContent
                        activeId={activeId}
                        addSkillToSearch={addSkillToSearch}
                        handleGlobalSearch={handleGlobalSearch}
                        jobLevelsResult={jobLevelsResult}
                        jobRolesResult={jobRolesResult}
                        result={displayedResult}
                        selectedSkillIds={selectedSkillIds}
                        setSearchTerm={setSearchTerm}
                        onCloseDropdown={onCloseDropdown}
                    />
                )}
            </GlobalSearchDropdownContainer>
        </Popper>
    );
};
